import * as EcdsaMultikey from '@digitalbazaar/ecdsa-multikey';
import * as ecdsaSd2023Cryptosuite from '@digitalbazaar/ecdsa-sd-2023-cryptosuite';
import * as Bls12381Multikey from '@digitalbazaar/bls12-381-multikey';
import * as bbs2023Cryptosuite from '@digitalbazaar/bbs-2023-cryptosuite';
import { DataIntegrityProof } from '@digitalbazaar/data-integrity';
import { CredentialContextVersion, getDocumentLoader } from '@trustvc/w3c-context';
import jsonldSignatures from 'jsonld-signatures';
import jsonldSignaturesV7 from 'jsonld-signatures-v7';
import * as jsonld from 'jsonld';
import { _checkCredential, getFirstContext, _checkKeyPair, prefilCredentialId } from './helper';
import { proofTypeMapping } from './types';

var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
const { createSignCryptosuite: createEcdsaSd2023SignCryptosuite, createDiscloseCryptosuite: createEcdsaSd2023DiscloseCryptosuite, createVerifyCryptosuite: createEcdsaSd2023VerifyCryptosuite } = ecdsaSd2023Cryptosuite;
const { createSignCryptosuite: createBbs2023SignCryptosuite, createDiscloseCryptosuite: createBbs2023DiscloseCryptosuite, createVerifyCryptosuite: createBbs2023VerifyCryptosuite } = bbs2023Cryptosuite;
const { purposes: { AssertionProofPurpose } } = jsonldSignatures;
const { purposes: { AssertionProofPurpose: AssertionProofPurposeV7 } } = jsonldSignaturesV7;
const isRawDocument = /* @__PURE__ */ __name((document) => {
  try {
    _checkCredential(document, void 0, "sign");
  } catch (err) {
    return false;
  }
  return typeof document === "object";
}, "isRawDocument");
const isRawDocumentV1_1 = /* @__PURE__ */ __name((document) => isRawDocument(document) && getFirstContext(document) === CredentialContextVersion.v1, "isRawDocumentV1_1");
const isRawDocumentV2_0 = /* @__PURE__ */ __name((document) => isRawDocument(document) && getFirstContext(document) === CredentialContextVersion.v2, "isRawDocumentV2_0");
const isSignedDocument = /* @__PURE__ */ __name((document) => {
  try {
    _checkCredential(document, void 0, "verify");
  } catch (err) {
    return false;
  }
  return typeof document === "object" && "proof" in document;
}, "isSignedDocument");
const isSignedDocumentV1_1 = /* @__PURE__ */ __name((document) => isSignedDocument(document) && getFirstContext(document) === CredentialContextVersion.v1, "isSignedDocumentV1_1");
const isSignedDocumentV2_0 = /* @__PURE__ */ __name((document) => isSignedDocument(document) && getFirstContext(document) === CredentialContextVersion.v2, "isSignedDocumentV2_0");
const isEcdsaSdBaseProof = /* @__PURE__ */ __name(async (proofValue) => {
  try {
    if (!proofValue || !proofValue.startsWith("u")) {
      return false;
    }
    const { decode } = await import('base64url-universal');
    const decoded = decode(proofValue.slice(1));
    return decoded.length >= 3 && Number(decoded[0]) === 217 && Number(decoded[1]) === 93 && Number(decoded[2]) === 0;
  } catch {
    return false;
  }
}, "isEcdsaSdBaseProof");
const isBbs2023BaseProof = /* @__PURE__ */ __name(async (proofValue) => {
  try {
    if (!proofValue || !proofValue.startsWith("u")) {
      return false;
    }
    const { decode } = await import('base64url-universal');
    const decoded = decode(proofValue.slice(1));
    if (decoded.length < 3 || Number(decoded[0]) !== 217 || Number(decoded[1]) !== 93) {
      return false;
    }
    const thirdByte = Number(decoded[2]);
    return thirdByte === 2 || thirdByte === 4 || thirdByte === 6 || thirdByte === 8;
  } catch {
    return false;
  }
}, "isBbs2023BaseProof");
const baseProofDetectors = {
  "ecdsa-sd-2023": isEcdsaSdBaseProof,
  "bbs-2023": isBbs2023BaseProof
};
const isDerived = /* @__PURE__ */ __name(async (document) => {
  if (document.proof?.type === "BbsBlsSignatureProof2020") {
    return true;
  } else if (document.proof?.type === "DataIntegrityProof") {
    const proof = jsonld.getValues(document, "proof")[0];
    const cryptosuite = proof.cryptosuite;
    if (cryptosuite === "ecdsa-sd-2023" || cryptosuite === "bbs-2023") {
      if (await baseProofDetectors[cryptosuite](proof.proofValue)) {
        return false;
      } else return true;
    }
    return false;
  }
  return false;
}, "isDerived");
const extractMandatoryPointers = /* @__PURE__ */ __name(async (proofValue, cryptosuite) => {
  try {
    const isBaseProof = cryptosuite === "ecdsa-sd-2023" ? await isEcdsaSdBaseProof(proofValue) : await isBbs2023BaseProof(proofValue);
    if (!isBaseProof) {
      return [];
    }
    const decodedProofValue = Buffer.from(proofValue.slice(1), "base64url");
    const cbor = await import('cbor');
    const components = cbor.decode(decodedProofValue.slice(3));
    if (cryptosuite === "ecdsa-sd-2023") {
      if (Array.isArray(components) && components.length === 5) {
        return components[4] || [];
      }
    } else if (cryptosuite === "bbs-2023") {
      if (Array.isArray(components) && components.length === 5) {
        return components[4] || [];
      }
    }
    return [];
  } catch (error) {
    return [];
  }
}, "extractMandatoryPointers");
const signCredential = /* @__PURE__ */ __name(async (credential, keyPair, cryptoSuite = "ecdsa-sd-2023", options) => {
  try {
    const documentLoader = options?.documentLoader ?? await getDocumentLoader();
    if (cryptoSuite === "BbsBlsSignature2020") {
      return {
        error: "BbsBlsSignature2020 is no longer supported. Please use the latest cryptosuite versions instead."
      };
    } else if (cryptoSuite === "ecdsa-sd-2023" || cryptoSuite === "bbs-2023") {
      _checkKeyPair(keyPair);
      _checkCredential(credential, void 0, "sign");
      const keyPairInstance = cryptoSuite === "ecdsa-sd-2023" ? await EcdsaMultikey.from({
        ...keyPair
      }) : await Bls12381Multikey.from({
        ...keyPair
      });
      const firstContext = credential["@context"][0];
      const isV2 = firstContext === CredentialContextVersion.v2;
      const coreMandatoryPointers = [
        "/issuer"
      ];
      if (isV2) {
        if (credential.validFrom) {
          coreMandatoryPointers.push("/validFrom");
        }
      } else {
        coreMandatoryPointers.push("/issuanceDate");
      }
      const userMandatoryPointers = options?.mandatoryPointers || [];
      const mandatoryPointers = [
        ...coreMandatoryPointers,
        ...userMandatoryPointers.filter((pointer) => !coreMandatoryPointers.includes(pointer))
      ];
      const cryptosuiteInstance = cryptoSuite === "ecdsa-sd-2023" ? createEcdsaSd2023SignCryptosuite({
        mandatoryPointers
      }) : createBbs2023SignCryptosuite({
        mandatoryPointers
      });
      const suite = new DataIntegrityProof({
        signer: keyPairInstance.signer(),
        cryptosuite: cryptosuiteInstance
      });
      credential = prefilCredentialId(credential, cryptoSuite);
      const signed = await jsonldSignatures.sign(credential, {
        suite,
        purpose: new AssertionProofPurpose(),
        documentLoader
      });
      return {
        signed
      };
    } else {
      return {
        error: `"${cryptoSuite}" is not supported.`
      };
    }
  } catch (err) {
    if (!(err instanceof Error)) {
      return {
        error: "An error occurred while signing the credential."
      };
    }
    const errorMessage = err?.message;
    if (errorMessage.includes("Dereferencing a URL did not result in a valid JSON-LD object")) {
      const errorUrl = err?.details?.url;
      return {
        error: `Dereferencing a URL did not result in a valid JSON-LD object: "${errorUrl || "Unknown URL"}"`
      };
    }
    return {
      error: errorMessage
    };
  }
}, "signCredential");
const verifyCredential = /* @__PURE__ */ __name(async (credential, options) => {
  try {
    _checkCredential(credential);
    const documentLoader = options?.documentLoader ?? await getDocumentLoader();
    let verificationResult;
    const proofType = jsonld.getValues(credential, "proof")[0].type;
    const SuiteClass = proofTypeMapping[proofType];
    if (SuiteClass) {
      if (proofType === "DataIntegrityProof") {
        const proof = jsonld.getValues(credential, "proof")[0];
        const cryptosuite = proof.cryptosuite;
        if (cryptosuite === "ecdsa-sd-2023" || cryptosuite === "bbs-2023") {
          const isBaseCredential = await baseProofDetectors[cryptosuite](proof.proofValue);
          if (isBaseCredential) {
            return {
              verified: false,
              error: `${cryptosuite} base credentials must be derived before verification. Use deriveCredential() first.`
            };
          }
          const verifyCryptosuite = cryptosuite === "ecdsa-sd-2023" ? createEcdsaSd2023VerifyCryptosuite() : createBbs2023VerifyCryptosuite();
          verificationResult = await jsonldSignatures.verify(credential, {
            suite: new DataIntegrityProof({
              cryptosuite: verifyCryptosuite
            }),
            purpose: new AssertionProofPurpose(),
            documentLoader
          });
        } else {
          return {
            verified: false,
            error: `DataIntegrityProof with cryptosuite "${cryptosuite}" is not supported for verification.`
          };
        }
      } else {
        verificationResult = await jsonldSignaturesV7.verify(credential, {
          suite: new SuiteClass(),
          purpose: new AssertionProofPurposeV7(),
          documentLoader
        });
      }
    } else {
      return {
        verified: false,
        error: `Proof type "${proofType}" is not supported for verification.`
      };
    }
    if (verificationResult.verified) {
      return {
        verified: true
      };
    } else if (verificationResult.error && verificationResult.error.errors) {
      const errorMessage = verificationResult.error.errors[0].message;
      if (errorMessage.includes("Unexpected token N in JSON at position 0") || errorMessage.includes("Cannot read properties of undefined (reading 'length')")) {
        return {
          verified: false,
          error: "Verification method could not be found."
        };
      }
      if (errorMessage.includes("Dereferencing a URL did not result in a valid JSON-LD object")) {
        const errorUrl = verificationResult.error.errors[0].details?.url;
        return {
          verified: false,
          error: `Dereferencing a URL did not result in a valid JSON-LD object: "${errorUrl || "Unknown URL"}"`
        };
      }
      return {
        verified: false,
        error: errorMessage
      };
    }
    return {
      verified: false,
      error: "Verification error."
    };
  } catch (err) {
    if (!(err instanceof Error)) {
      return {
        verified: false,
        error: "An error occurred while verifying the credential."
      };
    }
    return {
      verified: false,
      error: err.message
    };
  }
}, "verifyCredential");
const deriveCredential = /* @__PURE__ */ __name(async (credential, revealedAttributes, options) => {
  try {
    _checkCredential(credential);
    const documentLoader = options?.documentLoader ?? await getDocumentLoader();
    const proofType = jsonld.getValues(credential, "proof")[0].type;
    if (proofType === "BbsBlsSignature2020") {
      return {
        error: "BbsBlsSignature2020 is no longer supported for derivation. Please use the latest cryptosuite versions instead."
      };
    } else if (proofType === "DataIntegrityProof") {
      const proof = jsonld.getValues(credential, "proof")[0];
      const cryptosuite = proof.cryptosuite;
      if (cryptosuite === "ecdsa-sd-2023" || cryptosuite === "bbs-2023") {
        if (!Array.isArray(revealedAttributes)) {
          return {
            error: `${cryptosuite} requires revealedAttributes to be an array of JSON pointers (string[]).`
          };
        }
        const selectivePointers = revealedAttributes;
        const isAlreadyDerived = !await baseProofDetectors[cryptosuite](proof.proofValue);
        if (isAlreadyDerived) {
          return {
            error: `${cryptosuite} derived credentials cannot be further derived. Multiple rounds of derivation are not supported by this cryptosuite.`
          };
        }
        const mandatoryPointers = await extractMandatoryPointers(proof.proofValue, cryptosuite);
        const hasCredentialSubjectInMandatory = mandatoryPointers.some((pointer) => pointer.startsWith("/credentialSubject"));
        const hasCredentialSubjectInSelective = selectivePointers.some((pointer) => pointer.startsWith("/credentialSubject"));
        if (!hasCredentialSubjectInMandatory && !hasCredentialSubjectInSelective) {
          selectivePointers.push("/credentialSubject");
        }
        const cryptosuiteInstance = cryptosuite === "ecdsa-sd-2023" ? createEcdsaSd2023DiscloseCryptosuite({
          selectivePointers
        }) : createBbs2023DiscloseCryptosuite({
          selectivePointers
        });
        const suite = new DataIntegrityProof({
          cryptosuite: cryptosuiteInstance
        });
        const derivedProof = await jsonldSignatures.derive(credential, {
          suite,
          purpose: new AssertionProofPurpose(),
          documentLoader
        });
        return {
          derived: derivedProof
        };
      } else {
        return {
          error: `DataIntegrityProof with cryptosuite "${cryptosuite}" is not supported for derivation.`
        };
      }
    } else {
      return {
        error: `Proof type "${proofType}" is not supported for derivation.`
      };
    }
  } catch (err) {
    if (!(err instanceof Error)) {
      return {
        error: "An error occurred while deriving the proof."
      };
    }
    return {
      error: err.message
    };
  }
}, "deriveCredential");

export { deriveCredential, isDerived, isRawDocument, isRawDocumentV1_1, isRawDocumentV2_0, isSignedDocument, isSignedDocumentV1_1, isSignedDocumentV2_0, signCredential, verifyCredential };
